import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  ReloadTeamContext,
  TeamContext,
} from "@redotech/redo-web/context/team-context";
import { memo, useContext, useState } from "react";

const DISMISS_MS = 24 * 60 * 60 * 1000;

function dismissKey(teamId: string): string {
  return `redo.omsReactivationDismissedAt.${teamId}`;
}

function isDismissed(teamId: string): boolean {
  try {
    const dismissedAt = Number(localStorage.getItem(dismissKey(teamId)));
    return !!dismissedAt && Date.now() - dismissedAt < DISMISS_MS;
  } catch {
    return false;
  }
}

function recordDismissed(teamId: string): void {
  try {
    localStorage.setItem(dismissKey(teamId), String(Date.now()));
  } catch {
    return;
  }
}

export const OmsReactivationModal = memo(function OmsReactivationModal() {
  const team = useContext(TeamContext);
  const reloadTeam = useContext(ReloadTeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { t } = useTranslation();

  const [failed, setFailed] = useState(false);
  const [dismissedTeamId, setDismissedTeamId] = useState<string>();

  const handleDismiss = useHandler(() => {
    if (team) {
      recordDismissed(team._id);
      setDismissedTeamId(team._id);
    }
  });

  const handleReactivate = useHandler(async () => {
    setFailed(false);
    try {
      await rpcClient.reactivateOms(null);
      reloadTeam?.();
    } catch (error) {
      console.error("Failed to reactivate OMS", error);
      setFailed(true);
    }
  });

  const outboundLabels = team?.settings.outboundLabels;
  if (
    !team ||
    dismissedTeamId === team._id ||
    isDismissed(team._id) ||
    !outboundLabels?.newOmsEnabled ||
    !outboundLabels.omsInactiveSince
  ) {
    return null;
  }

  return (
    <RedoModal
      isOpen
      modalSize="sm"
      onModalCloseRequested={handleDismiss}
      primaryButton={{
        text: t("Reactivate OMS"),
        onClick: handleReactivate,
        size: "sm",
      }}
      title={t("OMS deactivated")}
    >
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          {t("OMS was deactivated due to inactivity. Reactivate?")}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {t(
            "After you reactivate, it will take some time for your orders to be up to date.",
          )}
        </Text>
        {failed && (
          <Text fontSize="sm" textColor="error">
            {t("Failed to reactivate OMS. Please try again.")}
          </Text>
        )}
      </Flex>
    </RedoModal>
  );
});
